import { Link } from 'react-router-dom';
import { CTA, PageHero, ProductShot } from '../../components/Common';
import { Seo } from '../../components/Seo';
import { capabilityMap } from '../../data/site';
import { Head } from './sections';

type SolutionProps = {
  /** The search-result title; the hero heading is written for people already on the page. */
  seoTitle: string;
  eyebrow: string;
  title: string;
  description: string;
  outcome: string;
  areas: string[];
  shot?: { slug: string; alt: string };
};

function Solution({ seoTitle, eyebrow, title, description, outcome, areas, shot }: SolutionProps) {
  const groups = capabilityMap.filter(group => areas.includes(group.area));
  return (
    <>
      <Seo title={seoTitle} breadcrumb={eyebrow} description={description} />
      <PageHero {...{ eyebrow, title, description }}>
        <div className="actions">
          <Link className="button primary" to="/request-demo">
            Request a demo
          </Link>
          <Link className="button secondary" to="/features">
            All features
          </Link>
        </div>
      </PageHero>
      {shot && (
        <section className="section compact">
          <div className="container">
            <ProductShot slug={shot.slug} alt={shot.alt} className="motion-item" />
          </div>
        </section>
      )}
      <section className="section">
        <div className="container grid two">
          {groups.map(group => (
            <article className="card capability" key={group.area}>
              <h2 className="card-title">{group.title}</h2>
              <p>{group.body}</p>
              <ul className="tick-list">
                {group.items.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
      <section className="section surface">
        <div className="container">
          <Head eyebrow="Operational outcome" title={outcome} />
        </div>
      </section>
      <CTA />
    </>
  );
}

export function FleetOperations() {
  return (
    <Solution
      seoTitle="Trip and dispatch management software"
      eyebrow="Fleet operations"
      title="Keep every trip, vehicle, and follow-up connected."
      description="A structured workspace for dispatch, fleet records, delivery status, fuel and toll spend, and proof-of-delivery work."
      outcome="Less time reconstructing what happened. More context for the next operational decision."
      areas={['operations', 'fleet']}
      shot={{
        slug: 'trips',
        alt: 'The trips list showing status, date, truck, driver, route, distance, party, and fare for every trip.',
      }}
    />
  );
}

export function FinanceReporting() {
  return (
    <Solution
      seoTitle="Transport finance and receivables software"
      eyebrow="Finance and reporting"
      title="Connect operational activity to financial control."
      description="One settlement ledger behind trips, receipts, and vouchers — with the receivables work ranked so the highest-impact call happens first."
      outcome="Give operations and finance a shared path from trip activity to settlement and review."
      areas={['finance', 'insights']}
      shot={{
        slug: 'action-center',
        alt: 'The owner action centre ranking parties by overdue value, oldest overdue days, pending trips, and expected collection.',
      }}
    />
  );
}

export function FleetVisibility() {
  return (
    <Solution
      seoTitle="Truck GPS tracking and fleet visibility"
      eyebrow="Fleet visibility"
      title="Turn vehicle activity into operational context."
      description="Live GPS positions, route and distance context, vehicle status, and follow-ups sitting beside the fleet records they belong to."
      outcome="Move from a location on a map to the trip, vehicle, and action behind it."
      areas={['fleet', 'operations']}
      shot={{
        slug: 'vehicles',
        alt: 'The vehicles list showing fleet records with document validity, status, and activity.',
      }}
    />
  );
}

export function WorkshopInventory() {
  return (
    <Solution
      seoTitle="Fleet workshop and inventory software"
      eyebrow="Workshop and inventory"
      title="Keep maintenance and parts on the same ledger as the fleet."
      description="Raise job cards against a vehicle, issue parts from FIFO-costed stock, and follow tyres as serialized assets from purchase to scrap."
      outcome="Know what a vehicle has cost to keep running, not just what it earned."
      areas={['fleet', 'insights']}
    />
  );
}
